import { SmartDocumentItemTypeEnum } from "../enums";

export const SmartDocumentInvisibleItemTypes = Object.keys(SmartDocumentItemTypeEnum).reduce((acc, key) => {
    if (!SmartDocumentItemTypeEnum[key].visible) {
        acc.push(SmartDocumentItemTypeEnum[key].value);
    }
    return acc;
}, []);

export const SmartDocumentConditionalItemTypes = Object.keys(SmartDocumentItemTypeEnum).reduce((acc, key) => {
    if (SmartDocumentItemTypeEnum[key].is_conditional) {
        acc.push(SmartDocumentItemTypeEnum[key].value);
    }
    return acc;
}, []);

export const SmartDocumentNonConditionalItemTypes = Object.keys(SmartDocumentItemTypeEnum).reduce((acc, key) => {
    if (SmartDocumentItemTypeEnum[key].is_condition_value) {
        acc.push(SmartDocumentItemTypeEnum[key].value);
    }
    return acc;
}, []);

export const SmartDocumentGroupableItemTypes = Object.keys(SmartDocumentItemTypeEnum).reduce((acc, key) => {
    if (SmartDocumentItemTypeEnum[key].is_groupable) {
        acc.push(SmartDocumentItemTypeEnum[key].value);
    }
    return acc;
}, []);

// SUGGESTED EDITS HELPERS (ported from legale_frontend util/helpers/smart_document.js)
export const suggestedEditsContentsToHtml = (contents) => {
    if (!Array.isArray(contents)) {
        return '';
    }

    return contents.map(nodeToHtml).join('');
}

const nodeToHtml = (node) => {
    if (!node || typeof node !== 'object') {
        return '';
    }

    // Text node
    if (typeof node.text === 'string') {
        let text = escapeHtml(node.text);

        // Apply normal formatting
        if (node.format) {
            text = applyFormats(text, node.format);
        }

        return text;
    }

    const tag = node.type;

    if (!tag) {
        return '';
    }

    // Attributes
    const attrs = node.attrs
        ? attributesToHtml(node.attrs)
        : '';

    // Children
    const children = Array.isArray(node.children)
        ? node.children.map(nodeToHtml).join('')
        : '';

    // Void HTML elements
    const voidElements = new Set([
        'area',
        'base',
        'br',
        'col',
        'embed',
        'hr',
        'img',
        'input',
        'link',
        'meta',
        'param',
        'source',
        'track',
        'wbr'
    ]);

    if (voidElements.has(tag)) {
        return `<${tag}${attrs}>`;
    }

    return `<${tag}${attrs}>${children}</${tag}>`;
}

const attributesToHtml = (attrs) => {
    return Object.entries(attrs)
        .filter(([name, value]) => value !== undefined && value !== null)
        .map(([name, value]) => {
            return ` ${name}="${escapeAttribute(value)}"`;
        })
        .join('');
}

const applyFormats = (text, formats) => {
    if (!Array.isArray(formats)) {
        return text;
    }

    return formats.reduce((result, format) => {
        if (typeof format === 'string') {
            switch (format) {
                case 'strong':
                    return `<strong>${result}</strong>`;

                case 'em':
                    return `<em>${result}</em>`;

                case 'underline':
                    return `<u>${result}</u>`;

                case 'strikethrough':
                    return `<s>${result}</s>`;

                case 'code':
                    return `<code>${result}</code>`;

                default:
                    return result;
            }
        }

        // Format object, e.g.:
        // {
        //   type: "a",
        //   attrs: { href: "..." }
        // }
        if (
            format &&
            typeof format === 'object' &&
            format.type
        ) {
            const attrs = format.attrs
                ? attributesToHtml(format.attrs)
                : '';

            return `<${format.type}${attrs}>${result}</${format.type}>`;
        }

        return result;
    }, text);
}

const escapeHtml = (value) => {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const escapeAttribute = (value) => {
    return escapeHtml(value);
}

export const resolveSuggestion = (model, suggestionId, action) => {
    if (!model || !Array.isArray(model.contents)) {
        throw new Error('Invalid Suggested Edits model');
    }

    if (!['accept', 'reject'].includes(action)) {
        throw new Error('action must be "accept" or "reject"');
    }

    const id = Number(suggestionId);

    const newModel = structuredClone(model);

    newModel.contents = newModel.contents
        .map(node => resolveNode(node, id, action))
        .filter(Boolean);

    // Remove resolved suggestion metadata
    if (newModel.history) {
        delete newModel.history[String(id)];
    }

    if (newModel.metadata) {
        delete newModel.metadata[String(id)];
    }

    return newModel;
}

const resolveNode = (node, suggestionId, action) => {
    if (!node || typeof node !== 'object') {
        return node;
    }

    const newNode = { ...node };

    /*
     * Node itself can have opData.
     *
     * Example:
     * {
     *   type: "h2",
     *   opData: {
     *      id: 7,
     *      type: "insert"
     *   },
     *   children: [...]
     * }
     */
    if (
        newNode.opData &&
        Number(newNode.opData.id) === suggestionId
    ) {
        const type = newNode.opData.type;

        if (type === 'insert') {
            // Accept insertion -> keep node
            // Reject insertion -> remove node
            if (action === 'reject') {
                return null;
            }

            delete newNode.opData;
        }

        else if (type === 'remove') {
            // Accept removal -> remove node
            // Reject removal -> keep node
            if (action === 'accept') {
                return null;
            }

            delete newNode.opData;
        }

        else if (type === 'modify') {
            applyModifyResolution(newNode, action);
            delete newNode.opData;
        }
    }

    /*
     * Text / child nodes
     */
    if (Array.isArray(newNode.children)) {
        newNode.children = newNode.children
            .map(child => resolveNode(child, suggestionId, action))
            .filter(Boolean);
    }

    /*
     * A text node can itself have opData.
     */
    if (
        newNode.opData &&
        Number(newNode.opData.id) === suggestionId
    ) {
        delete newNode.opData;
    }

    return newNode;
}

const applyModifyResolution = (node, action) => {
    if (action === 'accept') {
        // New formatting
        if (node.format !== undefined) {
            node.format = structuredClone(node.format);
        }

        // Remove old formatting information
        delete node.oldFormat;
    }

    if (action === 'reject') {
        // Restore old formatting
        if (node.oldFormat !== undefined) {
            node.format = structuredClone(node.oldFormat);
        } else {
            delete node.format;
        }

        delete node.oldFormat;
    }
}

export const getSuggestions = (model) => {
    const suggestions = new Map();
    const history = model?.history && typeof model.history === "object" ? model.history : {};

    const getFeedbacks = (suggestionId) => {
        if (suggestionId == null) return [];
        const key = String(suggestionId);
        const entries = history[key] ?? history[suggestionId];
        return Array.isArray(entries) ? entries : [];
    };

    const getText = (node) => {
        if (!node || typeof node !== "object") {
            return "";
        }

        if (typeof node.text === "string") {
            return node.text;
        }

        if (Array.isArray(node.children)) {
            return node.children.map(getText).join("");
        }

        return "";
    };

    function walk(node) {
        if (!node || typeof node !== "object") {
            return;
        }

        if (node.opData) {
            const op = node.opData;
            const id = String(op.id);
            const text = getText(node);

            if (!suggestions.has(id)) {
                suggestions.set(id, {
                    id: op.id,
                    uid: op.uid,
                    timestamp: op.timestamp,
                    operations: [],
                    oldText: "",
                    newText: "",
                    metadata: model?.metadata?.[id] ?? model?.metadata?.[op.id] ?? null,
                });
            }

            const suggestion = suggestions.get(id);

            suggestion.operations.push({
                type: op.type,
                text,
                format: node.format || null,
                oldFormat: node.oldFormat || null,
            });

            if (op.type === "remove") {
                suggestion.oldText += text;
            } else if (op.type === "insert") {
                suggestion.newText += text;
            } else if (op.type === "modify") {
                suggestion.oldText += text;
                suggestion.newText += text;
            }
        }

        if (Array.isArray(node.children)) {
            node.children.forEach(walk);
        }
    }

    (model?.contents ?? []).forEach(walk);

    return Array.from(suggestions.values())
        .map((suggestion) => {
            const types = new Set((suggestion.operations || []).map((op) => op.type));
            const hasInsert = types.has("insert");
            const hasRemove = types.has("remove");
            const hasModify = types.has("modify");

            let kind = "insert";
            if (hasModify && !hasInsert && !hasRemove) {
                kind = "modify";
            } else if (hasInsert && hasRemove) {
                kind = "replace";
            } else if (hasRemove) {
                kind = "remove";
            } else if (hasInsert) {
                kind = "insert";
            } else if (hasModify) {
                kind = "modify";
            }

            const feedbacks = getFeedbacks(suggestion.id);

            return {
                ...suggestion,
                kind,
                feedbacks,
                // Kept for existing UI that reads suggestion.feedback
                feedback: feedbacks,
            };
        })
        .sort((a, b) => a.id - b.id);
};

export const addHistoryToSuggestedEditsModel = (model, suggestionId, feedback, uid) => {
    if (!model || !suggestionId || !feedback || !uid) {
        return model;
    }

    const newModel = structuredClone(model);
    const key = String(suggestionId);
    
    if (!newModel.history) {
        newModel.history = {};
    }
    if (!newModel.history[key]) {
        newModel.history[key] = [];
    }

    newModel.history[key].push({
        id: newModel.history[key].length + 1,
        uid,
        timestamp: Date.now(),
        feedback,
    });
    return newModel;
}